const BASE_URL = 'https://aifluens.com';
const SITE_NAME = 'AI Fluens';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

export interface PageSEO {
  title: string;
  description: string;
  keywords?: string;
  noIndex?: boolean;
}

export const seoConfig: Record<string, PageSEO> = {
  '/': {
    title: 'How to Build an AI Agent | OpenAI and Claude Agent Builder Guides | AI Fluens',
    description:
      'Learn how to build and create AI agents using OpenAI, Claude, and modern AI agent builders. Explore step-by-step guides, tutorials, and practical resources to build intelligent AI agents with AI Fluens.',
    keywords:
      'How to Build an AI Agent, Build an AI Agent, How to Create an AI Agent, Create AI Agent, OpenAI Agent Builder, Agent Builder OpenAI, AI Agent Builders, Claude Agent Builder, Learning AI Agent',
  },
  '/skills': {
    title: 'Your AI Skills | AI Fluens',
    description:
      'View your personalized AI skill profile. See which competencies matter most for your role and where to focus your learning.',
  },
  '/how-it-works': {
    title: 'How AI Fluens Works — Build an AI Agent or Upskill Plan | AI Fluens',
    description:
      'See how AI Fluens works: build a real AI agent step by step, or follow a personalized week-by-week upskill plan — and what you walk away with.',
  },
  '/pricing': {
    title: 'Pricing | AI Fluens',
    description:
      'AI Fluens pricing — unlock personalized, week-by-week AI upskill plans with in-depth study material and hands-on exercises.',
  },
  '/faq': {
    title: 'AI Upskilling FAQ — What Engineers Should Learn in AI | AI Fluens',
    description:
      'Answers to common questions: what should a software engineer learn in AI, how to become an AI fluent engineer, and how AI Fluens creates personalized upskill plans.',
  },
  '/about': {
    title: 'Become an AI Fluent Engineer | About AI Fluens',
    description:
      'AI Fluens helps software engineers, engineering managers, and leaders become AI native professionals. Personalized AI upskill plans that close the skills gap faster.',
  },
  '/contact': {
    title: 'Contact Us | AI Fluens',
    description:
      'Get in touch with the AI Fluens team. We\'re here to answer questions about our AI upskilling plans.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | AI Fluens',
    description:
      'Read the AI Fluens privacy policy to understand how we collect, use, and protect your personal information.',
  },
  '/terms-of-use': {
    title: 'Terms of Use | AI Fluens',
    description:
      'Review the AI Fluens terms of use governing your access and use of our AI upskilling platform.',
  },
  '/refund-policy': {
    title: 'Refund Policy | AI Fluens',
    description:
      'Learn about our refund policy for the AI Fluens upskill plan, including eligibility, timelines, and how to request a refund.',
  },
  '/blog': {
    title: 'AI Engineering Blog — Guides for AI-Assisted Development | AI Fluens',
    description:
      'Practical articles on AI-assisted software engineering, AI agent architecture, performance optimization, and upskilling strategies for engineers and leaders.',
  },
  '/learn': {
    title: 'Learn to Build AI Agents — Free Hands-On Course | AI Fluens',
    description:
      'A free, hands-on course on building AI agents: LLM foundations, tool use, RAG, MCP, traces, evals, guardrails, and multi-agent delegation. Read each lesson, then build it for real.',
  },
  '/glossary': {
    title: 'AI Glossary — AI Engineering, Agents, RAG & Learning Terms | AI Fluens',
    description:
      'Definitions for engineers: AI agents, ReAct, MCP, RAG, context windows, prompting, inference, observability, guardrails, compliance (GDPR, EU AI Act), learning science, and more. Each term has a full article.',
  },
  '/sample-plan': {
    title: 'Sample AI Upskill Plan | AI Fluens',
    description:
      'Preview a sample personalized AI upskill plan. See what a week-by-week learning roadmap looks like before creating your own AI plan.',
  },
  '/ai-for-software-engineers': {
    title: 'AI for Software Engineers — Skills, Trends & How to Learn AI | AI Fluens',
    description:
      'AI for software engineers: build a personalized, role-based AI upskilling plan covering generative AI, AI-assisted coding, prompt engineering, RAG, AI agents, LLM app design, evaluation and guardrails — mapped to your stack and goals.',
  },
  '/ai-roadmap-for-engineering-managers': {
    title: 'AI Roadmap for Engineering Managers — Strategy, Governance & Team Enablement | AI Fluens',
    description:
      'AI roadmap for engineering managers: build a personalized plan covering AI strategy, implementation oversight, governance, team enablement, tool evaluation, and agentic AI adoption — mapped to your leadership context and goals.',
  },
  '/ai-learning-essentials-for-leaders': {
    title: 'AI Learning Essentials for Leaders — Executive AI Literacy & Business Impact | AI Fluens',
    description:
      'AI learning essentials for leaders: build a personalized plan covering AI business fundamentals, operations, innovation, productivity, governance, and investment decisions — mapped to your leadership role and organizational goals.',
  },
  // /sample-study-material/:topicKey — per-topic title/description set in SampleStudyMaterial.tsx (SEOHead props)
  // Protected routes — noIndex
  '/assessment': {
    title: 'Choose Assessment | AI Fluens',
    description: 'Choose your AI skill assessment type.',
    noIndex: true,
  },
  '/basic-assessment': {
    title: 'Basic Assessment | AI Fluens',
    description: 'Take the AI Fluens basic skill assessment.',
    noIndex: true,
  },
  '/basic-results': {
    title: 'Assessment Results | AI Fluens',
    description: 'View your basic assessment results.',
    noIndex: true,
  },
  '/advanced-assessment': {
    title: 'Advanced Assessment | AI Fluens',
    description: 'Take the AI Fluens advanced skill assessment.',
    noIndex: true,
  },
  '/advanced-results': {
    title: 'Advanced Results | AI Fluens',
    description: 'View your advanced assessment results.',
    noIndex: true,
  },
  '/upskill-plan': {
    title: 'Your Upskill Plan | AI Fluens',
    description: 'Your personalized AI upskilling plan.',
    noIndex: true,
  },
  '/payment': {
    title: 'Get Advanced Assessment | AI Fluens',
    description: 'Upgrade to the advanced AI skill assessment.',
    noIndex: true,
  },
  '/profile': {
    title: 'Your Profile | AI Fluens',
    description: 'Manage your AI Fluens profile.',
    noIndex: true,
  },
  '/resume-assessment': {
    title: 'Resume Assessment | AI Fluens',
    description: 'Resume your in-progress assessment.',
    noIndex: true,
  },
  '/assessment-progress': {
    title: 'Assessment Progress | AI Fluens',
    description: 'View your assessment progress.',
    noIndex: true,
  },
  '/auth-callback': {
    title: 'Signing In... | AI Fluens',
    description: 'Completing sign-in.',
    noIndex: true,
  },
  '/forgot-password': {
    title: 'Forgot Password | AI Fluens',
    description: 'Reset your AI Fluens account password.',
    noIndex: true,
  },
  '/reset-password': {
    title: 'Reset Password | AI Fluens',
    description: 'Set a new password for your AI Fluens account.',
    noIndex: true,
  },
};

export { BASE_URL, SITE_NAME, DEFAULT_IMAGE };
